import { formatIntegerBr, formatPercentDisplay } from './managerial-kpis'

export type OrderStatusId = 'paid' | 'pending' | 'canceled'

export type OrderStatusCounts = Record<OrderStatusId, number>

export type OrderStatusSlice = {
  id: OrderStatusId
  label: string
  color: string
  count: number
}

export type OrderStatusView = OrderStatusSlice & {
  percent: number
  formattedCount: string
  formattedPercent: string
}

export type OrderStatusBreakdown = {
  total: number
  formattedTotal: string
  slices: OrderStatusView[]
}

export const EMPTY_ORDER_STATUS_COUNTS: OrderStatusCounts = {
  paid: 0,
  pending: 0,
  canceled: 0,
}

export const ORDER_STATUS_META: Record<OrderStatusId, { label: string; color: string }> = {
  paid: { label: 'Pago', color: '#22c55e' },
  pending: { label: 'Pendente', color: '#f59e0b' },
  canceled: { label: 'Cancelado', color: '#f43f5e' },
}

const STATUS_ORDER: OrderStatusId[] = ['paid', 'pending', 'canceled']

export function roundPercentsToHundred(values: number[]): number[] {
  if (values.length === 0) return []

  const rounded = values.map((value) => Math.round(value * 10) / 10)
  const total = Number(rounded.reduce((sum, value) => sum + value, 0).toFixed(1))
  const diff = Number((100 - total).toFixed(1))
  if (diff === 0) return rounded

  let maxIndex = 0
  for (let index = 1; index < rounded.length; index += 1) {
    if ((rounded[index] ?? 0) > (rounded[maxIndex] ?? 0)) {
      maxIndex = index
    }
  }

  const current = rounded[maxIndex] ?? 0
  rounded[maxIndex] = Number((current + diff).toFixed(1))
  return rounded
}

export function buildOrderStatusBreakdown(counts: OrderStatusCounts): OrderStatusBreakdown {
  const slicesBase = STATUS_ORDER.map((id) => ({
    id,
    label: ORDER_STATUS_META[id].label,
    color: ORDER_STATUS_META[id].color,
    count: counts[id],
  }))

  const total = slicesBase.reduce((sum, slice) => sum + slice.count, 0)
  const rawPercents =
    total === 0
      ? slicesBase.map(() => 0)
      : slicesBase.map((slice) => (slice.count / total) * 100)
  const percents = total === 0 ? rawPercents : roundPercentsToHundred(rawPercents)

  return {
    total,
    formattedTotal: formatIntegerBr(total),
    slices: slicesBase.map((slice, index) => ({
      ...slice,
      percent: percents[index] ?? 0,
      formattedCount: formatIntegerBr(slice.count),
      formattedPercent: formatPercentDisplay(percents[index] ?? 0),
    })),
  }
}
